<template>
  <v-list id="ra-list" dense class="pa-0" :height="listHeight" :style="{ 'overflow-y': 'auto' }">
    <progress-linear :active="loading"></progress-linear>
    <v-list-item v-if="raList.length == 0">
      <v-list-item-title class="pt-2 text-center">{{ $t('sod.msg.no.vltn') }}</v-list-item-title>
    </v-list-item>
    <!-- eslint-disable-next-line prettier/prettier -->
    <v-list-group
      v-for="(ra, rIdx) in raList" v-model="raList[rIdx].child.active" :key="rIdx"
      append-icon="" no-action eager class="border-u-wrap"
      @click="fetchAuthList(rIdx)"
    >
      <template v-slot:activator>
        <v-list-item-title class="py-2">
          <v-icon>{{ expandIcon(raList[rIdx].child.active) }}</v-icon>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <span v-on="on" class="pl-4">
                <span class="pr-2" :style="riskLevelColorStyle(ra.riskLevel)">{{ raInfo(ra.riskType, ra.riskLevel) }}</span>
                {{ ra.raNm }}
              </span>
            </template>
            <span>{{ ra.raDesc }}</span>
          </v-tooltip>
        </v-list-item-title>
      </template>
      <template #default>
        <v-list-item>
          <v-list-item-content>
            <v-row>
              <v-col cols="12">
                <v-list-item-title class="pb-2">{{ $t('sod.ttl.ra.auth') }}</v-list-item-title>
                <!-- eslint-disable-next-line prettier/prettier -->
                <v-data-table
                  :headers="headers" :items="ra.child.auths" item-key="authId"
                  fixed-header dense hide-default-footer disable-pagination
                />
              </v-col>
            </v-row>
          </v-list-item-content>
        </v-list-item>
        <v-list id="ra-inner-list" dense class="pa-0 pl-8">
          <v-list-group v-for="(auth, aIdx) in raList[rIdx].child.authList" :key="aIdx" append-icon="" no-action eager class="border-n-wrap">
            <template v-slot:activator>
              <v-list-item-title class="d-flex py-2">
                <v-img v-if="usage == 'SR'" @click="openAuthDetail(auth)" src="@/assets/image/diagram/node_single_role_icon.png" class="item-image" />
                <v-img v-if="usage == 'CR'" @click="openAuthDetail(auth)" src="@/assets/image/diagram/node_composite_role_icon.png" class="item-image" />
                <v-img v-if="usage == 'US'" @click="openAuthDetail(auth)" src="@/assets/image/diagram/node_user_icon.png" class="item-image" />
                <v-tooltip v-if="usage != 'US'" top>
                  <template v-slot:activator="{ on }">
                    <span v-on="on" class="pl-4">{{ auth.authNm }}</span>
                  </template>
                  <span>{{ auth.authNmDesc }}</span>
                </v-tooltip>
                <span v-if="usage == 'US'" class="pl-4">{{ auth.authNm }}</span>
              </v-list-item-title>
            </template>
          </v-list-group>
        </v-list>
      </template>
    </v-list-group>
  </v-list>
</template>

<script>
import { api } from '@/api/common/instances';

export default {
  props: {
    usage: {
      type: String,
      required: true,
    },
    time: {
      type: Number,
      required: true,
    },
    type: {
      type: String,
      required: true,
    },
    height: {
      type: Number,
    },
  },
  data() {
    return {
      raList: [],
      loading: false,
    };
  },
  computed: {
    systemId() {
      return this.$route.params.systemId;
    },
    listHeight() {
      return this.height - 34;
    },
    headers() {
      return [
        { text: this.$t('sod.ttl.id'), value: 'authId', width: '70px', sortable: false, align: 'right' },
        { text: this.$t('sod.ttl.auth.obj') + ' / ' + this.$t('sod.ttl.prfl'), value: 'authObj', sortable: false },
        { text: this.$t('sod.ttl.auth.fld'), value: 'authFld', sortable: false },
        { text: this.$t('sod.ttl.auth.val'), value: 'authVal', sortable: false },
      ];
    },
  },
  watch: {
    time: {
      handler() {
        this.fetchRaList();
      },
    },
    type: {
      async handler() {
        await this.fetchRaList();
      },
    },
  },
  methods: {
    expandIcon(active) {
      if (active) return 'mdi-chevron-up';
      else return 'mdi-chevron-down';
    },
    raInfo(riskType, riskLevel) {
      if (riskType == 'P') {
        const riskTypeNm = this.$t(`sod.risk.level.${riskLevel}`);
        return `(${riskTypeNm} - ${this.$t('sod.ttl.profile')})`;
      } else {
        const riskTypeNm = this.$t(`sod.risk.level.${riskLevel}`);
        return `(${riskTypeNm})`;
      }
    },
    riskLevelColorStyle(riskLevel) {
      if (this.$vuetify.theme.isDark) {
        if (riskLevel == 'H') return 'color: #FF9800';
        else if (riskLevel == 'M') return 'color: #FF6F00';
        else if (riskLevel == 'L') return 'color: #E65100';
        else return 'color: #FFEB3B';
      } else {
        if (riskLevel == 'H') return 'color: #FF6F00';
        else if (riskLevel == 'M') return 'color: #FF9800';
        else if (riskLevel == 'L') return 'color: #FFEB3B';
        else return 'color: #E65100';
      }
    },
    async fetchAuthList(raIndex) {
      // 이미 해당 RA 위배 롤 목록이 존재하면 그냥 리턴한다.
      if (this.raList[raIndex].child.authList !== undefined) return;

      // RA 위배 롤 목록 조회
      this.loading = true;
      const ra = this.raList[raIndex];
      const { data: authList } = await api.get('/sod/dashboard/ra-vltn-auth-list', {
        params: {
          sstmId: this.systemId,
          analYmdhms: this.time,
          authType: this.type,
          raId: ra.raId,
        },
      });
      ra.child.authList = authList;

      // RA의 권한 정보 조회
      // eslint-disable-next-line prettier/prettier
      const { data: { items: auths}} = await api.get('/sod/ra-auth-list', {
        params: {
          raId: ra.raId,
          page: 1,
          limit: 2147483646, // Java의 Integer.MAX_VALUE에서 1 뺀 값.
        },
      });
      ra.child.auths = auths;
      this.loading = false;
    },
    async fetchRaList() {
      // 이 컴포넌트 인스턴스의 역할에 맞는 AUTH_TYPE이 들어왔을 때만 동작하고
      // 그렇지 않으면 데이터 조회하지 않는다.
      if (this.usage != this.type) return;
      // time 값이 넘어오지 않으면 데이터를 조회할 수 없으니 time이 초기화되기 전이면 그냥 리턴한다.
      if (this.time == 0) return;

      this.loading = true;
      // eslint-disable-next-line prettier/prettier
      const { data } = await api.get('/sod/dashboard/ra-vltn-ra-list', {
        params: {
          sstmId: this.systemId,
          analYmdhms: this.time,
          authType: this.type,
        },
      });
      data.forEach(ra => {
        ra.child = {
          active: undefined,
          authList: undefined,
          auths: undefined,
        };
      });
      this.raList = data;
      this.loading = false;
    },
    async openAuthDetail(auth) {
      // console.log('in AnalyzedSodList.vue, in openAuthDetail(), auth=', auth);
      const { data } = await api.get('/sod/dashboard/sod-anal-hist', {
        params: {
          analYmdhms: this.time,
        },
      });
      let modelType = auth.authType == 'SR' ? 'SROL' : auth.authType == 'CR' ? 'CROL' : 'USER';
      const params = {
        ymd: data.rtrckYmdhms,
        sstm: data.analSstmId,
        mand: data.analMandt,
        entt: modelType,
        name: auth.authNm,
      };
      const url = '/views/extra/auth/rtrck-search-detail';
      const options = 'width=1600, height=846';
      window.open(`${url}?${new URLSearchParams(params).toString()}`, '_blank', options);
    },
  },
};
</script>

<!--
 <style> 섹션에 scoped 속성을 넣으면
 .v-list-item--active 처럼 vuetify에 의해 동적으로 적용되는 클래스가 인식되질 않는다.
 그래서 scoped를 없애긴 했는데 그렇게 되면 아래의 모든 클래스가 어플리케이션 전역에 적용되게 된다.
 그래서 아래 클래스를 사용하는 요소들의 최상위 <div>에 id로 'sodra-list'를 부여하고
 그 자식들에게서만 적용되도록 하였다.
 2026.02.11.KWShin.
-->
<style lang="scss">
div#ra-list.theme--dark {
  .border-u-wrap {
    border: 2px solid #3a3a3a;
    border-top: none;
  }
}
div#ra-list.theme--light {
  .theme--light .border-u-wrap {
    border: 2px solid #dddddd;
    border-top: none;
  }
}
div#ra-list {
  .theme--dark .border-n-wrap {
    border: 1px solid #3a3a3a;
    border-right: none;
    border-bottom: none;
  }
  .theme--light .border-n-wrap {
    border: 1px solid #dddddd;
    border-right: none;
    border-bottom: none;
  }
  .theme--dark.v-list-item--active {
    background-color: #313131;
    .v-list-item__title {
      font-size: 1.6em;
    }
  }
  .theme--light.v-list-item--active {
    background-color: #f3f3f3;
    .v-list-item__title {
      font-size: 1.6em;
    }
  }
  div#ra-inner-list {
    .theme--dark.v-list-item--active .v-list-item__title {
      font-size: 1em;
    }
    .theme--light.v-list-item--active .v-list-item__title {
      font-size: 1em;
    }
  }
  .item-image {
    height: 16px;
    width: 16px;
    vertical-align: middle;
    flex: none;
  }
}
</style>
